import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Camera, IndianRupee, ShoppingCart, Users, Video } from "lucide-react";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Button } from "@groot/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { EmptyState } from "@groot/ui/empty-state";
import { listRowClass } from "@groot/ui/list-row";
import { SkeletonList } from "@groot/ui/loading-skeleton";
import { StatTile } from "@groot/ui/stat-tile";
import { Text } from "@groot/ui/typography";
import { cn } from "@groot/ui/lib/utils";
import { OrderStatusBadge, PaymentStatusBadge } from "@/components/status-badges";
import { useOrders } from "@/pages/order/hooks/useOrders";
import { useCustomers } from "@/pages/customer/hooks/useCustomers";
import { usePhotoRequests } from "@/pages/photo-request/hooks/usePhotoRequests";
import { useMomentRequests } from "@/pages/moment-request/hooks/useMomentRequests";
import { CustomerReplies } from "@/pages/print-batch/components/CustomerReplies";

/** Home dashboard: order / customer / request stats plus the latest orders. */
export function Dashboard() {
  const { data: orders, isLoading: ordersLoading } = useOrders();
  const { data: customers, isLoading: customersLoading } = useCustomers();
  const { data: photoRequests, isLoading: photoRequestsLoading } = usePhotoRequests();
  const { data: momentRequests, isLoading: momentRequestsLoading } = useMomentRequests();
  const navigate = useNavigate();

  const totalOrders = orders?.length ?? 0;
  const activeOrders =
    orders?.filter((o) => o.status !== "delivered" && o.status !== "cancelled").length ?? 0;
  const pendingPayments = orders?.filter((o) => o.paymentStatus === "not_paid").length ?? 0;
  const totalRevenue =
    orders
      ?.filter((o) => o.paymentStatus === "paid")
      .reduce((sum, o) => sum + Number(o.total), 0) ?? 0;
  const pendingPhotoRequests = photoRequests?.filter((r) => r.status === "pending").length ?? 0;
  const pendingMomentRequests = momentRequests?.filter((r) => r.status === "pending").length ?? 0;

  const recentOrders = orders?.slice(0, 5) ?? [];

  const stats = [
    {
      label: "Total Orders",
      icon: ShoppingCart,
      value: ordersLoading ? "--" : totalOrders,
      meta: `${activeOrders} active`,
    },
    {
      label: "Customers",
      icon: Users,
      value: customersLoading ? "--" : (customers?.length ?? 0),
    },
    {
      label: "Revenue (Paid)",
      icon: IndianRupee,
      value: `₹${totalRevenue.toLocaleString()}`,
      meta: `${pendingPayments} pending payment${pendingPayments !== 1 ? "s" : ""}`,
    },
    {
      label: "Photo Requests",
      icon: Camera,
      value: photoRequestsLoading ? "--" : (photoRequests?.length ?? 0),
      meta: `${pendingPhotoRequests} pending`,
    },
    {
      label: "Moments",
      icon: Video,
      value: momentRequestsLoading ? "--" : (momentRequests?.length ?? 0),
      meta: `${pendingMomentRequests} pending`,
    },
  ];

  const quickActions = [
    { label: "Manage Customers", icon: Users, to: "/customers" },
    { label: "Manage Orders", icon: ShoppingCart, to: "/orders" },
    { label: "Photo Requests", icon: Camera, to: "/photo-requests" },
    { label: "Moments", icon: Video, to: "/moment-requests" },
  ];

  return (
    <PageLayout title="Dashboard" description="Overview of your business">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-5">
        {stats.map(({ label, icon: Icon, value, meta }) => (
          <Card key={label}>
            <CardContent className="space-y-1">
              <StatTile label={label} icon={<Icon className="size-3.5" />} value={value} />
              {meta && <Text variant="meta">{meta}</Text>}
            </CardContent>
          </Card>
        ))}
      </div>

      <CustomerReplies />

      <Card className="pb-0">
        <CardHeader>
          <CardTitle>Recent Orders</CardTitle>
          <CardAction>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/orders">
                View all <ArrowRight className="size-4" />
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        {ordersLoading ? (
          <SkeletonList rows={5} leading="none" secondaryLine className="border-t border-border" />
        ) : recentOrders.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="No orders yet"
            className="border-t border-border"
            action={<Button onClick={() => navigate("/orders")}>Create your first order</Button>}
          />
        ) : (
          <ul className="divide-y divide-border border-t border-border">
            {recentOrders.map((order) => (
              <li key={order.id}>
                <Link
                  to={`/orders/${order.id}`}
                  className={cn(listRowClass, "items-start justify-between sm:items-center")}
                >
                  <div className="min-w-0 space-y-1">
                    <Text className="block font-medium whitespace-nowrap">{order.orderNumber}</Text>
                    <Text variant="meta" className="block truncate">
                      {order.customer?.name} · {new Date(order.orderDate).toLocaleDateString()}
                    </Text>
                    {/* Phones: badges under the name instead of squeezing the row */}
                    <div className="flex flex-wrap gap-2 pt-1 sm:hidden">
                      <OrderStatusBadge status={order.status} />
                      <PaymentStatusBadge status={order.paymentStatus} />
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <div className="hidden items-center gap-3 sm:flex">
                      <OrderStatusBadge status={order.status} />
                      <PaymentStatusBadge status={order.paymentStatus} />
                    </div>
                    <Text className="font-medium tabular-nums">₹{order.total}</Text>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        {quickActions.map(({ label, icon: Icon, to }) => (
          <Button
            key={to}
            variant="outline"
            className="flex h-auto flex-col gap-2 py-4"
            onClick={() => navigate(to)}
          >
            <Icon className="size-5" />
            {label}
          </Button>
        ))}
      </div>
    </PageLayout>
  );
}
